import React, { useEffect, useMemo, useRef, useState } from "react";

function escapeRegExp(value) {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function highlightTitle(title, searchTerm) {
  if (!searchTerm || searchTerm.trim().length < 3) {
    return title;
  }

  const pattern = searchTerm
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map((word) => escapeRegExp(word))
    .join("|");

  if (!pattern) {
    return title;
  }

  return title.replace(new RegExp(`(${pattern})`, "ig"), "<span class='highlight'>$1</span>");
}

function matchWords(subject, words) {
  const lowerSubject = subject.toLowerCase();
  return words.every((word) => lowerSubject.includes(word.toLowerCase()));
}

function sortResults(items) {
  return [...items].sort((a, b) => {
    if (a.itemType > b.itemType) return 1;
    if (a.itemType < b.itemType) return -1;

    if (a.itemType === 1 && b.itemType === 1) {
      return a.title.toLowerCase().localeCompare(b.title.toLowerCase());
    }

    if (a.popularity != null && b.popularity != null) {
      if (a.popularity > b.popularity) return 1;
      if (a.popularity < b.popularity) return -1;
    }

    return 0;
  });
}

export default function HeaderAutosuggestIsland({ config }) {
  const [filterData, setFilterData] = useState([]);
  const [filterSearch, setFilterSearch] = useState(config.initialSearchTerm || "");
  const [showResult, setShowResult] = useState(false);
  const [hasLoadedData, setHasLoadedData] = useState(false);
  const [hasUserTyped, setHasUserTyped] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (inputRef.current && config.initialSearchTerm) {
      inputRef.current.value = config.initialSearchTerm;
    }
  }, [config.initialSearchTerm]);

  useEffect(() => {
    const shouldFetch =
      hasUserTyped && filterSearch.length >= config.minSearchLength && !hasLoadedData;

    if (!shouldFetch) {
      if (filterSearch.length < config.minSearchLength) {
        setShowResult(false);
      }
      return;
    }

    let isActive = true;

    fetch(config.autoSuggestUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json; charset=utf-8",
      },
      body: JSON.stringify({ searchTerm: "" }),
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Autosuggest request failed with ${response.status}`);
        }

        return response.json();
      })
      .then((result) => {
        if (!isActive) {
          return;
        }

        setFilterData(sortResults(Array.isArray(result) ? result : []));
        setHasLoadedData(true);
      })
      .catch(() => {
        if (!isActive) {
          return;
        }

        setFilterData([]);
        setHasLoadedData(true);
        setShowResult(false);
      });

    return () => {
      isActive = false;
    };
  }, [config.autoSuggestUrl, config.minSearchLength, filterSearch, hasLoadedData, hasUserTyped]);

  const filteredItems = useMemo(() => {
    const terms = filterSearch.split(/(\s+)/).filter((value) => value.trim().length > 0);
    const items = filterData.filter((item) => matchWords(item.title, terms));

    if (filterSearch.length >= config.minSearchLength && items.length > 0) {
      return items.slice(0, config.limitOfAutoSuggest);
    }

    return [];
  }, [config.limitOfAutoSuggest, config.minSearchLength, filterData, filterSearch]);

  useEffect(() => {
    if (filterSearch.length < config.minSearchLength) {
      setShowResult(false);
      return;
    }

    setShowResult(hasUserTyped && filteredItems.length > 0);
  }, [config.minSearchLength, filterSearch, filteredItems, hasUserTyped]);

  const searchResultsUrl = `${config.searchResultsBaseUrl}?term=${filterSearch}`;

  const clearSearch = () => {
    setFilterSearch("");
    setShowResult(false);
    if (inputRef.current) {
      inputRef.current.value = "";
      inputRef.current.focus();
    }
  };

  const closeResults = () => {
    window.setTimeout(() => {
      setShowResult(false);
    }, 200);
  };

  const goToSearchResults = () => {
    if (filterSearch.length >= 1) {
      window.location.href = searchResultsUrl;
    }
  };

  return (
    <label>
      <input
        ref={inputRef}
        type="search"
        aria-label={config.ariaLabel}
        placeholder={config.placeholder}
        className="form-control"
        id="HeaderInput"
        autoComplete="off"
        defaultValue={config.initialSearchTerm}
        onChange={(event) => {
          setHasUserTyped(true);
          setFilterSearch(event.target.value);
        }}
        onBlur={closeResults}
        onKeyUp={(event) => {
          if (event.key === "Enter") {
            goToSearchResults();
          }
        }}
      />
      <div className="searchResult" style={{ display: showResult ? "block" : "none" }}>
        {filteredItems.map((item, index) => (
          <a
            key={`${item.title}-${index}`}
            href={`${config.searchResultsBaseUrl}?term=${item.title}`}
            className="name"
            dangerouslySetInnerHTML={{ __html: highlightTitle(item.title, filterSearch) }}
          />
        ))}
      </div>
      {filterSearch.length > 0 ? (
        <button type="button" onClick={clearSearch} className="clear-search">
          {config.clearLabel}
        </button>
      ) : null}
      <button type="button" onClick={goToSearchResults} className="search-button">
        <span className="icon-search" />
      </button>
    </label>
  );
}
